import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { messagesApi, uploadsApi } from '../services/api';
import MessageAttachments from '../components/MessageAttachments';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageSquare,
  Send,
  Plus,
  AlertTriangle,
  PhoneOff,
  Megaphone,
  Reply,
  Loader2,
  Users,
  User,
  Shield,
  UserCog,
  RefreshCw,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

const Messages = () => {
  const { user, selectedLocation, locations, isAdmin } = useAuth();
  const [messagesList, setMessagesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const [newMessage, setNewMessage] = useState({
    type: 'message',
    subject: '',
    content: '',
    is_urgent: false,
    recipient_type: 'location_group',
    recipient_id: '',
    recipient_name: '',
    attachments: [],
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const userRole = user?.role || '';
  const userRoles = user?.roles || [userRole];
  const isEmployee = userRole === 'employee';
  const isManagerOrAdmin = userRoles.some(r => ['admin', 'manager', 'accounting_admin', 'assistant_manager'].includes(r));
  const hasAdminRole = userRoles.some(r => ['admin', 'accounting_admin'].includes(r)) || (isAdmin && isAdmin());

  useEffect(() => {
    loadMessages();
  }, [selectedLocation]);

  // Auto-poll for new messages every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const msgs = await messagesApi.getAll({ locationId: selectedLocation });
        setMessagesList(msgs);
      } catch (e) { console.error('Message polling error:', e); }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedLocation]);

  useEffect(() => {
    if (showNewMessage) {
      loadRecipients();
    }
  }, [showNewMessage]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const msgs = await messagesApi.getAll({ locationId: selectedLocation });
      setMessagesList(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const response = await messagesApi.getRecipients();
      setRecipients(response.recipients || []);
    } catch (error) {
      console.error('Failed to load recipients:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const filteredMessages = useMemo(() => {
    let filtered = messagesList;
    if (activeTab !== 'all') {
      filtered = filtered.filter(m => m.type === activeTab);
    }
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [messagesList, activeTab]);

  const topLevelMessages = useMemo(
    () => filteredMessages.filter(m => !m.reply_to),
    [filteredMessages]
  );

  const replyCountByMessageId = useMemo(() => {
    const counts = {};
    messagesList.forEach(m => {
      if (m.reply_to) counts[m.reply_to] = (counts[m.reply_to] || 0) + 1;
    });
    return counts;
  }, [messagesList]);

  const unreadCount = filteredMessages.filter(m => !m.read_by?.includes(user?.id)).length;
  const urgentCount = filteredMessages.filter(m => m.is_urgent && !m.read_by?.includes(user?.id)).length;

  const handleRecipientChange = (recipientId) => {
    const recipient = recipients.find(r => r.id === recipientId);
    if (recipient) {
      setNewMessage({
        ...newMessage,
        recipient_type: recipient.type,
        recipient_id: recipientId,
        recipient_name: recipient.name,
      });
    }
  };

  const handleSendMessage = async () => {
    try {
      let locationId = selectedLocation !== 'all' ? selectedLocation : null;
      
      // Determine location ID based on recipient
      if (newMessage.recipient_type === 'location_group') {
        locationId = newMessage.recipient_id;
      } else if (isEmployee) {
        locationId = user.location_id || user.locationId;
      }
      
      await messagesApi.create({
        ...newMessage,
        location_id: locationId,
      });
      await loadMessages();
      setShowNewMessage(false);
      setNewMessage({ 
        type: 'message', 
        subject: '', 
        content: '', 
        is_urgent: false,
        recipient_type: 'location_group',
        recipient_id: '',
        recipient_name: '',
        attachments: [],
      });
    } catch (error) {
      alert('Failed to send message: ' + error.message);
    }
  };

  const handleAttachFile = async (file) => {
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const uploaded = await uploadsApi.upload(file);
      setNewMessage((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), uploaded],
      }));
    } catch (e) {
      alert('Upload failed: ' + e.message);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (fileId) => {
    setNewMessage((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((a) => a.id !== fileId),
    }));
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    try {
      await messagesApi.create({
        location_id: selectedThread.location_id,
        type: 'message',
        subject: `Re: ${selectedThread.subject}`,
        content: replyText,
        is_urgent: false,
        reply_to: selectedThread.id,
        recipient_type: selectedThread.recipient_type || 'location_group',
        recipient_id: selectedThread.sender_id, // Reply to the sender
        recipient_name: selectedThread.sender_name,
      });
      await loadMessages();
      setReplyText('');
    } catch (error) {
      alert('Failed to send reply: ' + error.message);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await messagesApi.markAsRead(messageId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'announcement': return <Megaphone className="w-4 h-4" />;
      case 'callout': return <PhoneOff className="w-4 h-4" />;
      case 'group_chat': return <Users className="w-4 h-4" />;
      case 'message': return <MessageSquare className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'announcement': return 'bg-blue-100 text-blue-700';
      case 'callout': return 'bg-red-100 text-red-700';
      case 'group_chat': return 'bg-green-100 text-green-700';
      case 'message': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRecipientIcon = (iconType) => {
    switch (iconType) {
      case 'users': return <Users className="w-4 h-4 mr-2 text-green-600" />;
      case 'megaphone': return <Megaphone className="w-4 h-4 mr-2 text-blue-600" />;
      case 'shield': return <Shield className="w-4 h-4 mr-2 text-purple-600" />;
      case 'user-cog': return <UserCog className="w-4 h-4 mr-2 text-amber-600" />;
      default: return <User className="w-4 h-4 mr-2 text-gray-600" />;
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return format(date, 'h:mm a');
    if (isYesterday(date)) return `Yesterday ${format(date, 'h:mm a')}`;
    return format(date, 'MMM d, h:mm a');
  };

  const getThreadMessages = (threadId) => {
    const thread = messagesList.find(m => m.id === threadId);
    if (!thread) return [];
    const replies = messagesList.filter(m => m.reply_to === threadId);
    return [thread, ...replies].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  // Group recipients by type for better UI
  const groupedRecipients = useMemo(() => {
    const groups = {
      groups: recipients.filter(r => r.type === 'location_group' || r.type === 'all_locations'),
      individuals: recipients.filter(r => r.type === 'individual'),
    };
    return groups;
  }, [recipients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
          <button onClick={loadMessages} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700" title="Refresh messages" data-testid="refresh-messages-btn">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
          <p className="text-gray-500 mt-1">
            {isEmployee ? 'Communicate with your manager' : 'Team communication and announcements'}
          </p>
        </div>
        <Button onClick={() => setShowNewMessage(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="new-message-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Unread</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{unreadCount}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl"><MessageSquare className="w-6 h-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Urgent</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{urgentCount}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-xl"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Announcements</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{filteredMessages.filter(m => m.type === 'announcement').length}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-xl"><Megaphone className="w-6 h-6 text-purple-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Call Outs</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{filteredMessages.filter(m => m.type === 'callout').length}</p>
              </div>
              <div className="bg-amber-100 p-3 rounded-xl"><PhoneOff className="w-6 h-6 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <Card className="lg:col-span-2 border-0 shadow-md">
          <CardHeader className="pb-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="announcement">Announcements</TabsTrigger>
                <TabsTrigger value="message">Messages</TabsTrigger>
                <TabsTrigger value="callout">Call Outs</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {filteredMessages.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {topLevelMessages.map((message) => {
                    const isRead = message.read_by?.includes(user?.id);
                    const replyCount = replyCountByMessageId[message.id] || 0;
                    
                    return (
                      <div
                        key={message.id}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!isRead ? 'bg-blue-50/50' : ''} ${selectedThread?.id === message.id ? 'bg-purple-50' : ''}`}
                        onClick={() => { setSelectedThread(message); markAsRead(message.id); }}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-gray-200">
                              {message.sender_name?.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">{message.sender_name}</span>
                              {message.recipient_name && (
                                <span className="text-xs text-gray-500">
                                  → {message.recipient_name}
                                </span>
                              )}
                              <Badge className={`text-xs ${getTypeColor(message.type)}`}>
                                {getTypeIcon(message.type)}
                                <span className="ml-1 capitalize">{message.type}</span>
                              </Badge>
                              {message.is_urgent && (
                                <Badge className="bg-red-500 text-white text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />Urgent
                                </Badge>
                              )}
                              {!isRead && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                            </div>
                            <p className="font-medium text-gray-800 truncate">{message.subject}</p>
                            <p className="text-sm text-gray-600 truncate">{message.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                              <span>{formatMessageTime(message.created_at)}</span>
                              {message.attachments?.length > 0 && (
                                <span className="flex items-center gap-1" data-testid={`attachment-indicator-${message.id}`}>
                                  <Paperclip className="w-3 h-3" />{message.attachments.length}
                                </span>
                              )}
                              {replyCount > 0 && (
                                <span className="flex items-center gap-1">
                                  <Reply className="w-3 h-3" />{replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No messages found</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Detail */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{selectedThread ? 'Conversation' : 'Select a message'}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {selectedThread ? (
              <>
                <ScrollArea className="h-[350px] p-4">
                  <div className="space-y-4">
                    {getThreadMessages(selectedThread.id).map((msg) => (
                      <div key={msg.id} className={`p-3 rounded-lg ${msg.sender_id === user?.id ? 'bg-purple-100 ml-8' : 'bg-gray-100 mr-8'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-gray-900">{msg.sender_id === user?.id ? 'You' : msg.sender_name}</span>
                          <span className="text-xs text-gray-400">{formatMessageTime(msg.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                        <MessageAttachments attachments={msg.attachments} />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="p-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    <Input placeholder="Type a reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendReply()} />
                    <Button onClick={handleSendReply} className="bg-purple-600 hover:bg-purple-700" disabled={!replyText.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a message to view the conversation</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Message Modal */}
      <Dialog open={showNewMessage} onOpenChange={setShowNewMessage}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Message</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {/* Recipient Selector */}
            <div className="space-y-2">
              <Label>Send To <span className="text-red-500">*</span></Label>
              {loadingRecipients ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading recipients...
                </div>
              ) : (
                <Select value={newMessage.recipient_id} onValueChange={handleRecipientChange}>
                  <SelectTrigger><SelectValue placeholder="Select recipient..." /></SelectTrigger>
                  <SelectContent>
                    {/* Group Chats */}
                    {groupedRecipients.groups.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Group Chats</div>
                        {groupedRecipients.groups.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            <span className="flex items-center">
                              {getRecipientIcon(r.icon)}
                              {r.name}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {/* Individual Recipients */}
                    {groupedRecipients.individuals.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">Individuals</div>
                        {groupedRecipients.individuals.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            <span className="flex items-center">
                              {getRecipientIcon(r.icon)}
                              {r.name}
                              {r.role && <Badge variant="outline" className="ml-2 text-xs">{r.role}</Badge>}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Message Type */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newMessage.type} onValueChange={(value) => setNewMessage({ ...newMessage, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="message"><span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Message</span></SelectItem>
                  {hasAdminRole && <SelectItem value="announcement"><span className="flex items-center gap-2"><Megaphone className="w-4 h-4" /> Announcement</span></SelectItem>}
                  {isEmployee && <SelectItem value="callout"><span className="flex items-center gap-2"><PhoneOff className="w-4 h-4" /> Call Out / Late</span></SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={newMessage.subject} onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })} placeholder="Enter subject..." />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={newMessage.content} onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })} placeholder="Type your message..." rows={4} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="urgent" checked={newMessage.is_urgent} onChange={(e) => setNewMessage({ ...newMessage, is_urgent: e.target.checked })} className="rounded border-gray-300" />
              <Label htmlFor="urgent" className="text-sm text-gray-600 cursor-pointer">Mark as urgent</Label>
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-sm">Attachments <span className="text-xs text-gray-400">(images, PDFs, docs — max 10 MB each)</span></Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="msg-attachment-input"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleAttachFile(f);
                    e.target.value = '';
                  }}
                  data-testid="message-attachment-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('msg-attachment-input')?.click()}
                  disabled={uploadingAttachment}
                  data-testid="message-attach-btn"
                >
                  {uploadingAttachment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
                  {uploadingAttachment ? 'Uploading…' : 'Attach file or picture'}
                </Button>
              </div>
              {newMessage.attachments?.length > 0 && (
                <ul className="space-y-1 text-xs" data-testid="message-attachments-list">
                  {newMessage.attachments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      <span className="flex items-center gap-2 truncate">
                        {a.is_image ? <ImageIcon className="w-3 h-3 text-blue-500" /> : <FileText className="w-3 h-3 text-gray-500" />}
                        <span className="truncate" title={a.original_filename}>{a.original_filename}</span>
                        <span className="text-gray-400 flex-shrink-0">({Math.round(a.size / 1024)} KB)</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(a.id)}
                        className="text-red-500 hover:text-red-700 text-xs ml-2"
                        aria-label="Remove attachment"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessage(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} className="bg-purple-600 hover:bg-purple-700" disabled={!newMessage.subject || !newMessage.content || !newMessage.recipient_id || uploadingAttachment} data-testid="send-message-btn">
              <Send className="w-4 h-4 mr-2" />Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
